import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { api, type ReviewerInvitationPublic } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { Button } from "../../design/components/Button.js";
import { Field, PasswordField } from "../../design/components/Field.js";
import { Alert } from "../../design/components/Feedback.js";
import { BrandMark } from "../../design/components/BrandMark.js";

type Step = "loading" | "decision" | "onboarding" | "sign-in" | "complete" | "declined" | "error";

function dateLabel(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

export function InvitationResponsePage({ token, finishExisting = false }: { token: string; finishExisting?: boolean }) {
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<ReviewerInvitationPublic | null>(null);
  const [step, setStep] = useState<Step>("loading");
  const [busy, setBusy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    void api.reviewerInvitation(token).then(async (result) => {
      if (!active) return;
      setInvitation(result);
      if (finishExisting && result.account_exists && result.state === "ACCEPTED_PENDING_ACCOUNT") {
        try {
          const accepted = await api.acceptExistingReviewerInvitation(token);
          if (active) navigate(accepted.destination, { replace: true });
        } catch (reason) {
          if (active) {
            setError(apiErrorMessage(reason, "Sign in with the invited address to finish."));
            setStep("sign-in");
          }
        }
        return;
      }
      if (result.state === "DECLINED") setStep("declined");
      else if (result.state === "ACCEPTED") setStep("complete");
      else if (result.state === "ACCEPTED_PENDING_ACCOUNT") setStep(result.account_exists ? "sign-in" : "onboarding");
      else if (result.state === "PENDING") setStep("decision");
      else setStep("error");
    }).catch(() => {
      if (!active) return;
      setError("This invitation is unavailable or no longer active.");
      setStep("error");
    });
    return () => { active = false; };
  }, [finishExisting, navigate, token]);

  const respond = async (response: "ACCEPT" | "DECLINE") => {
    if (response === "ACCEPT" && !acceptedTerms) {
      setError("Confirm the confidentiality terms before accepting.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.respondToReviewerInvitation(token, { response, accept_confidentiality: response === "ACCEPT" });
      if (response === "DECLINE") setStep("declined");
      else setStep(invitation?.account_exists ? "sign-in" : "onboarding");
    } catch (reason) {
      setError(apiErrorMessage(reason, "Your response could not be saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const onboard = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await api.onboardReviewer(token, {
        full_name: String(values.get("full_name") ?? ""),
        affiliation: String(values.get("affiliation") ?? ""),
        expertise_tags: String(values.get("expertise") ?? "").split(",").map((tag) => tag.trim()).filter(Boolean),
        preferred_capacity: Number(values.get("preferred_capacity") ?? 0),
        password: String(values.get("password") ?? ""),
      });
      setStep("complete");
    } catch (reason) {
      setError(apiErrorMessage(reason, "Reviewer access could not be created. Please check the form."));
    } finally {
      setBusy(false);
    }
  };

  const loginNext = encodeURIComponent(`/reviewer-invitations/accept?token=${encodeURIComponent(token)}&finish=1`);
  const workspaceDestination = invitation?.destination ?? "/my/reviews";

  return <main className="reviewer-invitation-page">
    <section className="reviewer-invitation-card">
      <BrandMark />
      {step === "loading" ? <p className="reviewer-invitation-loading" role="status">Opening invitation…</p> : null}
      {step === "error" ? <Alert tone="danger">{error || "This invitation is unavailable or no longer active."}</Alert> : null}
      {invitation && step !== "loading" && step !== "error" ? <>
        <header>
          <p className="reviewer-invitation-eyebrow">Reviewer invitation</p>
          <h1>{invitation.conference.name}</h1>
          <p><span>{invitation.expected_work} expected reviews</span> · Respond by {dateLabel(invitation.closes_at)}</p>
        </header>
        {step === "decision" ? <div className="reviewer-invitation-decision">
          <div className="reviewer-invitation-terms">
            <strong>Before you accept</strong>
            <p>{invitation.confidentiality_summary}</p>
            <label><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /><span>I agree to the confidentiality terms</span></label>
          </div>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <div className="reviewer-invitation-actions">
            <Button type="button" busy={busy} onClick={() => void respond("ACCEPT")}>Accept invitation</Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void respond("DECLINE")}>Decline</Button>
          </div>
        </div> : null}
        {step === "onboarding" ? <form className="reviewer-onboarding-form" onSubmit={onboard}>
          <div><h2>Create your reviewer access</h2><p>Your email is already secured to this invitation. Add the details used for review assignments.</p></div>
          <Field label="Full name" name="full_name" autoComplete="name" required />
          <Field label="Affiliation" name="affiliation" autoComplete="organization" />
          <Field label="Expertise" name="expertise" helper="Separate areas with commas." />
          <Field label="Preferred review capacity" name="preferred_capacity" type="number" min="0" max="10000" defaultValue={invitation.expected_work} required />
          <PasswordField label="Password" name="password" autoComplete="new-password" minLength={8} required helper="Use a strong password you do not use elsewhere." />
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Button type="submit" busy={busy}>Create reviewer access</Button>
        </form> : null}
        {step === "sign-in" ? <div className="reviewer-invitation-result">
          <h2>Finish with your existing account</h2>
          <p>This address already belongs to a Conferis account. Sign in with it to securely attach this invitation.</p>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <a className="ui-button ui-button--primary" href={`/login?next=${loginNext}`}><span className="ui-button__content">Sign in to finish</span></a>
        </div> : null}
        {step === "complete" ? <div className="reviewer-invitation-result">
          <h2>Reviewer access is ready</h2>
          <p>The invitation is accepted. Your assigned reviews will appear in your reviewer workspace.</p>
          <a className="ui-button ui-button--primary" href={`/login?next=${encodeURIComponent(workspaceDestination)}`}><span className="ui-button__content">Log in to continue</span></a>
        </div> : null}
        {step === "declined" ? <div className="reviewer-invitation-result"><h2>Invitation declined</h2><p>Your response has been recorded. No reviewer access was created.</p></div> : null}
      </> : null}
    </section>
  </main>;
}
